import { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight, Share2, Calendar, Clock, User, Tag } from 'lucide-react';
import ContactPopup from './ContactPopup';

// Header Component
const Header = ({ openContactPopup }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black bg-opacity-90 py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          {/* Logo */}
          <div className="flex items-center">
            <svg 
              width="40" 
              height="32" 
              viewBox="0 0 100 80" 
              className="fill-current text-red-600"
            >
              <path d="M50,0 L95,60 L80,60 L50,20 L20,60 L5,60 L50,0 Z M40,70 L60,70 L60,80 L40,80 L40,70 Z" />
            </svg>
            <span className="ml-2 text-xl font-bold text-white">PHOEN<span className="text-red-600">AI</span>X</span>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <a href="/#about" className="text-white hover:text-red-500 transition-colors">Home</a>
          <a href="/projects/1" className="text-white hover:text-red-500 transition-colors">Projects</a>
          <a href="/solutions" className="text-white hover:text-red-500 transition-colors">Solutions</a>
          <button 
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            onClick={openContactPopup}
          >
            Contact Us
          </button>
        </nav>
      </div>
    </header>
  );
};

// Hero Section for Project Detail
const ProjectHero = ({ project, goToHome }) => {
  return (
    <section className="relative pt-32 pb-16 bg-black">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-black via-black to-red-900 opacity-40"
        style={{ 
          backgroundImage: 'radial-gradient(circle at top right, rgba(224, 31, 38, 0.2), transparent 70%)' 
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <button 
          onClick={goToHome}
          className="inline-flex items-center text-gray-400 hover:text-red-500 mb-6 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Projects
        </button>
        
        <div className="max-w-4xl">
          <span className="text-red-500 text-sm font-medium mb-2 block">{project.category}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{project.title}</h1>
          <p className="text-xl-semibold text-gray-300 mb-8">{project.description}</p>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
            <div className="flex items-center">
              <Calendar size={16} className="mr-2" />
              {project.date}
            </div>
            <div className="flex items-center">
              <Clock size={16} className="mr-2" />
              {project.readTime}
            </div>
            <div className="flex items-center">
              <User size={16} className="mr-2" />
              {project.author}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Featured Image Section
const FeaturedImage = ({ project }) => {
  return (
    <section className="bg-black py-8">
      <div className="container mx-auto px-4">
        <div className="relative rounded-lg overflow-hidden">
          <img 
            src={project.image} 
            alt={project.title} 
            className="w-full object-cover h-96 md:h-auto"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
        </div>
      </div>
    </section>
  );
};

// Project Content Section
const ProjectContent = ({ project, openContactPopup }) => {
  return (
    <section className="bg-black py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content */}
          <div className="lg:w-2/3">
            <div className="prose prose-invert prose-red max-w-none">
              <p className="text-xl text-gray-300 mb-8">{project.fullDescription}</p>
              
              {project.content.map((block, index) => {
                if (block.type === 'paragraph') {
                  return <p key={index} className="text-xl-gray-300 mb-6">{block.content}</p>;
                } else if (block.type === 'heading') {
                  return <h2 key={index} className="text-2xl font-bold text-white mt-12 mb-6">{block.content}</h2>;
                } else if (block.type === 'image') {
                  return (
                    <div key={index} className="my-12">
                      <img src={block.src} alt={block.alt} className="w-full rounded-lg" />
                      {block.caption && <p className="text-sm text-gray-400 mt-2 text-center">{block.caption}</p>}
                    </div>
                  );
                } else if (block.type === 'list') {
                  return (
                    <ul key={index} className="list-disc pl-6 mb-8 space-y-2 text-gray-300">
                      {block.items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  );
                } else if (block.type === 'numbered-list') {
                  return (
                    <ol key={index} className="list-decimal pl-6 mb-8 space-y-2 text-gray-300">
                      {block.items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ol>
                  );
                } else if (block.type === 'callout') {
                  return (
                    <div key={index} className="bg-gray-900 border-l-4 border-red-600 p-6 my-8 rounded-r-lg">
                      <p className="text-white italic">{block.content}</p>
                    </div>
                  );
                }
                return null;
              })}
            </div>
            
            {/* Call to Action */}
            <div className="mt-16 bg-gradient-to-r from-gray-900 to-black p-8 rounded-lg border border-gray-800">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to transform your business with AI?</h3>
              <p className="text-gray-300 mb-6">Schedule a demo to see how our solutions can address your specific challenges.</p>
              <div className="flex flex-wrap gap-4">
                <button 
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded flex items-center"
                  onClick={openContactPopup}
                >
                  Schedule a Demo <ChevronRight className="ml-2" size={20} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:w-1/3">
            <div className="sticky top-32">
              <div className="bg-gray-900 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-white mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {project.tags && project.tags.map((tag, index) => (
                    <span key={index} className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm flex items-center">
                      <Tag size={12} className="mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-900 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Related Projects</h3>
                <div className="space-y-4">
                  {[
                    { id: 2, title: 'Natural Language Processing Suite' },
                    { id: 5, title: 'Autonomous Decision Engine' },
                    { id: 3, title: 'Predictive Analytics Platform' }
                  ].map((relatedProject) => (
                    <a key={relatedProject.id} href={`/projects/${relatedProject.id}`} className="block group">
                      <h4 className="text-white group-hover:text-red-500 transition-colors font-medium">{relatedProject.title}</h4>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ openContactPopup }) => {
  return (
    <footer className="bg-black text-white py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between mb-12">
          <div className="mb-8 md:mb-0">
            <div className="flex items-center mb-4">
              <svg 
                width="32" 
                height="24" 
                viewBox="0 0 100 80" 
                className="fill-current text-red-600"
              >
                <path d="M50,0 L95,60 L80,60 L50,20 L20,60 L5,60 L50,0 Z M40,70 L60,70 L60,80 L40,80 L40,70 Z" />
              </svg>
              <span className="ml-2 text-xl font-bold">PHOEN<span className="text-red-600">AI</span>X</span>
            </div>
            <p className="text-gray-400 max-w-md">
              Transforming industries through innovative AI solutions. 
              Our intelligent agents and applications power the future of business.
            </p>
          </div>
          
          <div className="w-full flex flex-col items-end">
  <div className="bg-gray-800 p-4 rounded-lg shadow-lg mt-auto">
    <h3 className="text-lg font-semibold mb-4 text-white">Contact Us</h3>
    <ul className="space-y-2">
      <li><a href="tel:+917200957597" className="text-gray-400 hover:text-red-500 transition-colors">+91 72009 57597</a></li>
      <li><a href="mailto:phoenaixworkspace@gmail.com" className="text-gray-400 hover:text-red-500 transition-colors">phoenaixworkspace@gmail.com</a></li>
      <li>
        <button 
          onClick={openContactPopup} 
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          Send us a message
        </button>
      </li>
    </ul>
  </div>
</div>
        </div>
        
        <div className="pt-8 border-t border-gray-800 text-center md:text-left flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">© 2025 PHOENAIX. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Terms of Service</a>
            <a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Project Detail Page Component
export default function ProjectDetailPage({ project, goToHome }) {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  
  const openContactPopup = () => {
    setIsContactPopupOpen(true);
  };
  
  const closeContactPopup = () => {
    setIsContactPopupOpen(false);
  };
  
  useEffect(() => {
    document.body.style.backgroundColor = '#000000';
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header openContactPopup={openContactPopup} />
      <ProjectHero project={project} goToHome={goToHome} />
      <FeaturedImage project={project} />
      <ProjectContent project={project} openContactPopup={openContactPopup} />
      <Footer openContactPopup={openContactPopup} />
      <ContactPopup isOpen={isContactPopupOpen} onClose={closeContactPopup} />
    </div>
  );
}